import React, { useState, useEffect, Fragment } from 'react'
import { Table, Button, Spin, Checkbox } from 'antd'
// import { timeFormat } from 'utils'
// import moment from 'moment'
import i18n from 'i18n'
import { SERVICE_NAME } from 'service'
import { useTranslation } from 'react-i18next'
import { store as reduxStore } from 'index'
import classNames from 'classnames'
import { getTempEnvParam } from 'env'
import { useIsMobile } from 'utils'
import { isGlobal } from 'conf'
import { confirmDelete, confirmDiscard, ArrowLeftIcon } from 'utils/ui'
import { SRC_TYPE, createUserData } from 'pages/records/components/helper'
import { isTotpRecordShareNoticeNeeded } from 'utils/plan'
import { getRecordServiceKey } from 'pages/records/components/MemoEditorModal/memoRecord'
import CardView from 'pages/records/components/CardView'
import { selectUser } from 'popups'
import getTableInfo from 'pages/records/tableInfo/sharedUsers'

import ResourceService from 'api/Resource'

import styles from 'pages/records/components/CardView/style.module.scss'
import styles0 from 'pages/records/components/style.module.scss'
import trash24SVG from '@/assets/images/trash-24.svg'
import plusIcon from '@/assets/images/icon-plus-16.svg'
import stylesLocal from './style.module.scss'

const saveSharedUsers = async (
  resourceId,
  shareList,
  encRsKey,
  messageApi,
  setLoading,
  onSuccess,
  // 사용자 노출 문구의 {{service}} 단어 키 — 메모 레코드면 "메모"(호출부가 getRecordServiceKey 로 결정)
  serviceKey = SERVICE_NAME,
  // 신규 공유 대상 알림 발송 여부 — 공유 패널 체크박스(기본 OFF). 신규/기존 판별은 서버가 upsert 결과로 수행
  sendNotification = false,
) => {
  setLoading(true)

  // new와 변경된 list
  const changedExpiredUserList = shareList.filter((item) => {
    return (
      item.sourceType === SRC_TYPE.ORGCHART_USER ||
      (item.sourceType === SRC_TYPE.SHARED_USER && item.newExpiredAt !== item.originData.expiredAt)
    )
  })

  const changedExpiredGroupList = shareList.filter((item) => {
    return (
      item.sourceType === SRC_TYPE.GROUP_ITEM ||
      (item.sourceType === SRC_TYPE.SHARED_GROUP && item.newExpiredAt !== item.originData.expiredAt)
    )
  })

  const changedPermissionUserIds = shareList
    .filter((item) => {
      return (
        item.sourceType === SRC_TYPE.SHARED_USER && item.permission !== item.originData.permission
      )
    })
    .map((item) => ({
      user_id: item.id,
      permission: item.permission,
    }))

  const changedPermissionGroupIds = shareList
    .filter((item) => {
      return (
        item.sourceType === SRC_TYPE.SHARED_GROUP && item.permission !== item.originData.permission
      )
    })
    .map((item) => ({
      group_id: item.id,
      permission: item.permission,
    }))

  setLoading(true)

  try {
    let successShown = false // 성공 메시지 표시 여부를 저장하는 변수

    if (changedPermissionUserIds.length > 0 || changedPermissionGroupIds.length > 0) {
      const result = await ResourceService.putSharePermission(resourceId, {
        user_permissions: changedPermissionUserIds,
        group_permissions: changedPermissionGroupIds,
      })

      if (result != null && result.code === 200) {
        if (onSuccess != null) {
          if (!successShown) {
            messageApi.open({
              type: 'success',
              content: i18n.t('group.result.editShare', { service: i18n.t(serviceKey) }),
            })
            successShown = true
          }
        }
      }
    }

    const expiredUserList = shareList.filter((item) => {
      return (
        item.sourceType === SRC_TYPE.SHARED_USER && item.newExpiredAt !== item.originData.expiredAt
      )
    })

    const expiredGroupList = shareList.filter((item) => {
      return (
        item.sourceType === SRC_TYPE.SHARED_GROUP && item.newExpiredAt !== item.originData.expiredAt
      )
    })
    if (
      expiredUserList.length > 0 ||
      expiredGroupList.length > 0 ||
      changedExpiredUserList.length > 0 ||
      changedExpiredGroupList.length > 0
    ) {
      let res = await ResourceService.prepareShare(resourceId, {
        user_emails: changedExpiredUserList.map((item) => item.email),
        group_ids: changedExpiredGroupList.map((item) => item.id),
      })

      if (res != null && res.code === 200) {
        const rsaEncDec = getTempEnvParam('rsaObj')
        const rsKeyObj = rsaEncDec.createRSKeyObj(encRsKey)

        const { user, group } = res.data
        const encUserRsKeys = user.keys.map((item) => {
          const matchingUser = changedExpiredUserList.find(
            (value) => value.email === item.user_email,
          )

          let firstName = null
          let lastName = null
          if (matchingUser.sourceType === SRC_TYPE.SHARED_USER) {
            firstName = matchingUser.name
          } else if (matchingUser.sourceType === SRC_TYPE.ORGCHART_USER) {
            const {
              family_name: familyName,
              given_name: givenName,
              display_name: displayName,
              user_name: userName,
            } = matchingUser.originData

            firstName = isGlobal() ? userName : familyName || displayName
            lastName = matchingUser.originData ? givenName || null : null
          }

          return {
            user_id: item.id,
            first_name: firstName,
            last_name: lastName,
            enc_user_rs_key: rsKeyObj.getEncRsKeyForShare(item.public_key),
            permission: matchingUser.permission || 1,
            expired_at:
              (matchingUser.newExpiredAt !== 0 ? matchingUser.newExpiredAt : null) ||
              (matchingUser.expiredAt != null ? null : matchingUser.expiredAt) ||
              null,
          }
        })

        const encGroupRsKeys = group.keys.map((item) => {
          const matchingGroup = changedExpiredGroupList.find((value) => value.id === item.id)
          return {
            group_id: item.id,
            enc_group_rs_key: rsKeyObj.getEncRsKeyForShare(item.group_public_key),
            permission: matchingGroup.permission || 1,
            expired_at:
              (matchingGroup.newExpiredAt !== 0 ? matchingGroup.newExpiredAt : null) ||
              (matchingGroup.expiredAt != null ? null : matchingGroup.expiredAt) ||
              null,
          }
        })

        res = await ResourceService.share(
          resourceId,
          {
            enc_user_rs_keys: encUserRsKeys,
            enc_group_rs_keys: encGroupRsKeys,
          },
          sendNotification,
        )

        if (res != null && res.code !== 200) {
          messageApi.open({
            type: 'error',
            content: res.message,
          })
          successShown = true
        }

        if (res != null && res.code === 200) {
          if (!successShown) {
            messageApi.open({
              type: 'success',
              content: i18n.t('group.result.editShare', { service: i18n.t(serviceKey) }),
            })
            successShown = true
          }
        }
      }
    }

    if (successShown) {
      onSuccess()
    }
  } catch (error) {
    console.log(error)
    const errorMessage =
      error.message.indexOf('Expiration time must be in the future') !== -1
        ? i18n.t('workspace.error.expirationTimeMustBeInTheFuture')
        : i18n.t('record.result.saveError')
    messageApi.open({
      type: 'error',
      content: errorMessage,
    })
  } finally {
    setLoading(false)
  }
}

export const deleteSharedUsers = async (
  resourceId,
  deleteList,
  shareList,
  setShareList,
  messageApi,
  onSuccess,
  // 사용자 노출 문구의 {{service}} 단어 키 — 메모 레코드면 "메모"(호출부가 getRecordServiceKey 로 결정)
  serviceKey = SERVICE_NAME,
) => {
  const deleteFunc = async () => {
    const deleteUserList = deleteList.filter((item) => {
      return item.sourceType === SRC_TYPE.SHARED_USER // shared user data인 경우에만 삭제 호출.
    })
    const userIds = deleteUserList.map((item) => item.id)

    const deleteGroupList = deleteList.filter((item) => {
      return item.sourceType === SRC_TYPE.SHARED_GROUP // shared user data인 경우에만 삭제 호출.
    })
    const groupIds = deleteGroupList.map((item) => item.id)

    try {
      let success = true
      if (userIds.length > 0 || groupIds.length > 0) {
        const res = await ResourceService.deleteShare(resourceId, {
          user_ids: userIds,
          group_ids: groupIds,
        })

        if (res != null && res.code === 200) {
          success = true
        } else {
          success = false
        }
      }

      if (success === true) {
        if (onSuccess != null) {
          onSuccess()
        }

        const list = shareList.filter((item) => {
          return !deleteList.some((value) => value.id === item.id)
        })

        setShareList([...list])

        messageApi.open({
          type: 'success',
          content: i18n.t('record.result.delete'),
        })
      }
    } catch (error) {
      console.log(error)
      messageApi.open({
        type: 'error',
        content: i18n.t('record.result.deleteError'),
      })
    }
  }
  const emailDeleteList = deleteList
    .map((item) => item.email || item.name)
    .join(', ')
    .replace(/^, /, '')

  confirmDelete({
    icon: 'red',
    title: i18n.t('common.button.confirm'),
    content: i18n.t('record.desc.deleteShare', {
      email: emailDeleteList,
      service: i18n.t(serviceKey),
    }),
    okText: i18n.t('record.button.unshare'),
    onOk: () => {
      deleteFunc()
    },
    onKeyDown: (e) => {
      if (e.key === 'Enter') {
        deleteFunc()
      }
    },
  })
}

const RecordSharing = ({
  openItem,
  id,
  messageApi,
  individual,
  orgChartData,
  reloadRecord,
  reload,
}) => {
  const [title, setTitle] = useState('')

  const [shareList, setShareList] = useState([])
  const [deleteList, setDeleteList] = useState([])

  const [resourceKey, setResourceKey] = useState(null)
  const [checkedKeys, setCheckedKeys] = useState([])

  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(true)
  // 신규 공유 대상 알림 발송 여부 — 기본 OFF (사용자 확정). 패널은 openItem 전환으로 unmount 되므로 재열기 시 자연 리셋
  const [notifyNewMembers, setNotifyNewMembers] = useState(false)

  const reduxState = reduxStore.getState()
  const { email, id: userId, name: userName } = reduxState.user
  const { t } = useTranslation()
  // owner 여부
  const isOwner = individual != null && individual.permission === SRC_TYPE.RESOURCE_OWNER

  // 기존 공유된 사용자 아닌 목록 추출
  const notShareList =
    shareList != null &&
    shareList.filter(
      (item) =>
        item.sourceType === SRC_TYPE.ORGCHART_USER || item.sourceType === SRC_TYPE.GROUP_ITEM || 0,
    )

  const cancelOnClick = () => {
    if (notShareList.length > 0) {
      confirmDiscard({
        title: t('group.title.discard'),
        content: t('group.desc.discardSharingMembers'),
        submit: () => {
          reload()
          reloadRecord()
          openItem('detail', id)
        },
      })
    } else {
      const filteredShareList =
        shareList != null &&
        shareList.filter((item) => item.sourceType !== SRC_TYPE.OWNER).map((item) => item.email)
      const emailList = individual != null && individual.share?.map((item) => item.email)
      const isMissing = emailList.length !== filteredShareList.length

      if (isMissing) {
        reloadRecord()
      }

      openItem('detail', id)
    }
  }

  const myData = {
    id: userId,
    email,
    name: userName,
    avatar_url: individual?.owner?.avatar_url ?? null,
  }

  useEffect(() => {
    if (individual != null) {
      const ownerData = createUserData(
        SRC_TYPE.OWNER,
        individual.permission === SRC_TYPE.RESOURCE_OWNER ? myData : individual.owner,
        orgChartData,
      )

      const list = individual.share
        .filter((item) => {
          return item.id !== individual.owner.id
        })
        .map((item) => {
          const { type } = item
          const srcType =
            type.toLowerCase() === 'group' ? SRC_TYPE.SHARED_GROUP : SRC_TYPE.SHARED_USER
          return createUserData(srcType, item, orgChartData)
        })

      setResourceKey(individual.resource_key)
      setTitle(individual.title)
      setShareList([ownerData, ...list])
    } else {
      setTitle('')
      setShareList([])
      setDeleteList([])
      setCheckedKeys([])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [individual])

  useEffect(() => {
    const saveEnabled =
      shareList != null &&
      shareList.some((item) => {
        if (item.sourceType === SRC_TYPE.ORGCHART_USER || item.sourceType === SRC_TYPE.GROUP_ITEM) {
          return true
        }

        if (item.sourceType === SRC_TYPE.SHARED_USER || item.sourceType === SRC_TYPE.SHARED_GROUP) {
          const permissionChanged = item.permission !== item.originData?.permission
          const expirationChanged =
            item.newExpiredAt != null &&
            item.newExpiredAt !== item.expiredAt &&
            !(item.newExpiredAt === 0 && item.originData?.expired_at === null)

          if (permissionChanged || expirationChanged) {
            return true
          }
        }

        return false
      })

    setDisabled(!saveEnabled)
  }, [shareList])

  const tableInfo = getTableInfo(styles0, shareList, setShareList)
  const isOwnerInDeleteList = deleteList.some((item) => item.email === email)
  const deleteEnabled =
    (deleteList.length === 1 && isOwnerInDeleteList === false) || deleteList.length > 1

  // share list table
  const tableAttrs = {
    className: classNames(styles0.userTable, 'smallTable', 'recordSharingTable'),
    onRow: tableInfo.onRow,
    pagination: false,
    showHeader: false,
    columns: tableInfo.columns,
    locale: { emptyText: null },
    dataSource: shareList,
    rowKey: (record) => {
      return record.id
    },
    rowSelection:
      isOwner === true
        ? {
            type: 'checkbox',
            checkStrictly: false,
            selectedRowKeys: checkedKeys,
            onSelect: (record, selected, selectedRows) => {
              console.log(' -- onSelect : ', record, selected, selectedRows)
              const newSelectedRows = selectedRows.filter(
                (item) => item != null && item.sourceType !== SRC_TYPE.OWNER,
              )
              const selectedRowKeys = newSelectedRows.map((row) => row.id)
              setCheckedKeys(selectedRowKeys)
              setDeleteList(newSelectedRows)
            },
          }
        : null,
    rowClassName: (record) => {
      const { sourceType } = record
      const checkDisabled = sourceType === SRC_TYPE.OWNER || sourceType === SRC_TYPE.RESOURCE_OWNER
      return classNames({ checkDisabled })
    },
  }

  // 현재 요금제로 만들 수 없는 항목(Basic 의 인증 코드)이 든 레코드는 **공유를 막지 않고
  // 안내만** 한다 (정책 확정 2026-08-26, 08-25 의 차단 철회): 공유는 같은 워크스페이스
  // 안에서만 되므로 수신자도 같은 등급이고, 그가 새로 얻는 건 Basic 에 이미 허용된 조회뿐이다.
  // 막으면 인수인계가 교착되고, 내보내기로 우회되면 감사 로그만 사라진다.
  const showTotpShareNotice = isTotpRecordShareNoticeNeeded(individual)

  const isMobile = useIsMobile()
  const openSelectUser = () => {
    const prevEmailList = []
    const prevGroupIds = []
    shareList.forEach((item) => {
      const { type } = item
      if (type === 'user') {
        prevEmailList.push(item.email)
      } else if (type === 'group') {
        prevGroupIds.push(item.id)
      }
    })

    selectUser({
      isMobile,
      userOnly: false,
      title: t('record.title.shareWith', { name: title }),
      excludeList: prevEmailList,
      excludeGroupIds: prevGroupIds,
      onOk: (data, closeFunc) => {
        console.log(' -- selected user : ', data)
        const newList = [...shareList]
        const { selectedUserList, selectedGroupList } = data
        selectedUserList.forEach((item) => {
          const user = createUserData(SRC_TYPE.ORGCHART_USER, item, orgChartData)
          newList.push(user)
        })
        selectedGroupList.forEach((item) => {
          const group = createUserData(SRC_TYPE.GROUP_ITEM, item, orgChartData)
          newList.push(group)
        })

        setShareList(newList)
        closeFunc()
      },
    })
  }

  const addShareButton = (
    <Button
      type="text"
      icon={<img src={plusIcon} alt="add" />}
      onClick={() => {
        openSelectUser()
      }}
    />
  )

  // trash button or plus button
  let buttonElem = null
  if (isOwner === true) {
    buttonElem = deleteEnabled ? (
      <Button
        type="text"
        icon={<img src={trash24SVG} alt="delete" />}
        onClick={async () => {
          if (deleteList.includes(email)) {
            deleteList.splice(deleteList.indexOf(email))
          }

          await deleteSharedUsers(
            id,
            deleteList,
            shareList,
            setShareList,
            messageApi,
            () => {
              reduxStore.dispatch({
                type: 'menu/RELOAD',
              })
              reload()
              setCheckedKeys([])
              setDeleteList([])
            },
            getRecordServiceKey(individual, SERVICE_NAME),
          )
        }}
      />
    ) : (
      addShareButton
    )
  }

  // header
  const header = (
    <Fragment>
      <div>
        {isMobile && <ArrowLeftIcon onClick={cancelOnClick} />}
        <b>{t('record.title.share', { name: title })}</b>
      </div>

      <div>{buttonElem}</div>
    </Fragment>
  )

  // submit button elements
  const btnsElem =
    isOwner === true ? (
      <div>
        <Button type="button" className={classNames(styles.CancelButton)} onClick={cancelOnClick}>
          {t('common.button.cancel')}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={disabled}
          className={classNames(styles.SaveButton, 'type1')}
          onClick={async () => {
            await saveSharedUsers(
              id,
              shareList,
              resourceKey.key,
              messageApi,
              setLoading,
              async () => {
                openItem('detail', id)
                const tablePage = getTempEnvParam('curTablePage')
                if (tablePage) {
                  tablePage.reload()
                }
              },
              getRecordServiceKey(individual, SERVICE_NAME),
              notifyNewMembers,
            )
          }}
        >
          {t('common.button.save')}
        </Button>
      </div>
    ) : (
      <div>
        <Button
          type="button"
          className={classNames(styles.CancelButton)}
          onClick={() => {
            openItem(null)
            reload()
          }}
        >
          {t('common.button.close')}
        </Button>
      </div>
    )

  const shouldShowNotify = isOwner && notShareList.length > 0

  return (
    <CardView header={header}>
      {showTotpShareNotice && (
        <div className={classNames(stylesLocal.totpShareNotice)} role="note">
          {t('record.totp.shareNotice')}
        </div>
      )}
      <div className={classNames(styles.RecordSharingContainer)}>
        <Spin spinning={loading}>
          <Table {...tableAttrs} />
        </Spin>
      </div>

      <div
        className={classNames(styles.ButtonContainer, {
          hidden: deleteEnabled,
          [stylesLocal.withNotify]: shouldShowNotify,
        })}
        style={shouldShowNotify ? undefined : { justifyContent: 'flex-end' }}
      >
        {shouldShowNotify && (
          <Checkbox
            checked={notifyNewMembers}
            onChange={(e) => setNotifyNewMembers(e.target.checked)}
            className={classNames(stylesLocal.notifyCheckbox)}
          >
            {t('record.share.notifyNewMembers')}
          </Checkbox>
        )}
        {btnsElem}
      </div>
    </CardView>
  )
}

export default RecordSharing
